import type { AxiosError, AxiosResponse } from 'axios'
import axios from 'axios'
import { ref } from 'vue'

import { FASTAPI_CONFIG } from '@/config/fastapi'
import { useAuthStore } from '@/stores/authStore'
import { isAbortError } from '@/utils/typeGuardUtil'

// FastAPI Backend Base URL
const FASTAPI_BASE_URL = FASTAPI_CONFIG.BASE_URL

const fastapiClient = axios.create({
  baseURL: FASTAPI_BASE_URL,
  timeout: 10000, // 10秒超时
  withCredentials: true, // 允许发送和接收 cookies
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add request interceptor to include auth token
fastapiClient.interceptors.request.use(
  (config) => {
    try {
      // Get token from Pinia authStore
      const authStore = useAuthStore()
      const token = authStore.getAccessToken()

      console.log('fastapiClient - Token:', token ? 'Found' : 'Not found')
      if (token) {
        console.log('fastapiClient - Adding Authorization header')
        config.headers.Authorization = `Bearer ${token}`
      } else {
        console.log(
          'fastapiClient - No token available, skipping Authorization header'
        )
      }
    } catch (error) {
      console.warn('fastapiClient - Failed to get token from authStore:', error)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Global function to handle 401 errors with LoginDialog
let loginDialogPromise: Promise<boolean> | null = null

async function handle401Error(): Promise<boolean> {
  // If there's already a login dialog open, return its promise
  if (loginDialogPromise) {
    return loginDialogPromise
  }

  // Create a promise that will be resolved when user logs in
  loginDialogPromise = new Promise((resolve) => {
    // Emit a custom event to show the login dialog
    const event = new CustomEvent('show-login-dialog', {
      detail: { resolve }
    })
    window.dispatchEvent(event)
  })

  const result = await loginDialogPromise
  loginDialogPromise = null
  return result
}

// Add response interceptor to handle 401 errors
fastapiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const authStore = useAuthStore()

        // If user is not initialized yet, don't show dialog - let router guard handle redirect
        if (!authStore.isInitialized || !authStore.currentUser) {
          console.log(
            'FastAPI Auth: 401 error during initialization, will redirect to login page'
          )
          return Promise.reject(error)
        }
        const loginSuccess = await handle401Error()
        if (loginSuccess) {
          // If login was successful, retry the original request
          const newToken = authStore.getAccessToken()
          console.log(
            'Login successful, retrying request with new token:',
            !!newToken
          )
          console.log('Auth store state:', {
            isAuthenticated: authStore.isAuthenticated,
            hasToken: !!authStore.getAccessToken(),
            hasUser: !!authStore.currentUser
          })

          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            console.log(
              'Retrying request to:',
              originalRequest.url,
              'with method:',
              originalRequest.method
            )
            try {
              const retryResult = await fastapiClient.request(originalRequest)
              console.log('Request retry successful:', retryResult.status)
              return retryResult
            } catch (retryError) {
              console.error('Request retry failed:', retryError)
              throw retryError
            }
          } else {
            console.error('No token available after login success')
          }
        } else {
          console.log('Login was cancelled or failed')
        }
      } catch (error) {
        console.error('Login dialog error:', error)
      }
    }

    return Promise.reject(error)
  }
)

export interface User {
  id: number
  username: string
  email: string
  bio?: string
  avatar_url?: string
  phone?: string
  permission_level: number
  member_type: number
  member_expires_at?: string
  points: number
  wallet_balance: number
  referrer_id?: number
  unblock_time?: string
  is_active: boolean
  is_admin: boolean
  created_at: string
  updated_at?: string
  last_login?: string
  max_tasks: number
}

export interface LoginRequest {
  username: string
  password: string
}

export interface RegisterRequest {
  username: string
  email: string
  password: string
  bio?: string
  avatar_url?: string
  phone?: string
  wechat_openid?: string
  wechat_unionid?: string
  referrer_id?: number
}

export interface UserUpdateRequest {
  username?: string
  email?: string
  password?: string
  bio?: string
  avatar_url?: string
  phone?: string
  wechat_openid?: string
  wechat_unionid?: string
  permission_level?: number
  member_type?: number
  member_expires_at?: string
  points?: number
  wallet_balance?: number
  referrer_id?: number
  unblock_time?: string
}

export interface PasswordChangeRequest {
  current_password: string
  new_password: string
}

export interface ApiResponse {
  success: boolean
  message: string
  data?: any
}

export interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
}

export interface ApiResponse {
  success: boolean
  message: string
  data?: any
}

/**
 * Service for interacting with FastAPI authentication backend
 */
export const useFastApiAuthService = () => {
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // 手动重置加载状态的方法
  const resetLoadingState = () => {
    isLoading.value = false
    error.value = null
  }

  const handleApiError = (
    err: unknown,
    context: string,
    routeSpecificErrors?: Record<number, string>
  ): string => {
    if (!axios.isAxiosError(err))
      return err instanceof Error
        ? `${context}: ${err.message}`
        : `${context}: Unknown error occurred`

    const axiosError = err as AxiosError<ApiResponse>

    if (axiosError.response) {
      const { status, data } = axiosError.response

      if (routeSpecificErrors && routeSpecificErrors[status])
        return routeSpecificErrors[status]

      switch (status) {
        case 400:
          return `Bad request: ${data?.message || 'Invalid input'}`
        case 401:
          return 'Unauthorized: Authentication required'
        case 403:
          return `Forbidden: ${data?.message || 'Access denied'}`
        case 404:
          return `Not found: ${data?.message || 'Resource not found'}`
        case 409:
          return `Conflict: ${data?.message || 'Resource conflict'}`
        case 500:
          return `Server error: ${data?.message || 'Internal server error'}`
        default:
          return `${context}: ${data?.message || axiosError.message}`
      }
    }

    return `${context}: ${axiosError.message}`
  }

  const executeRequest = async <T>(
    requestCall: () => Promise<AxiosResponse<T>>,
    options: {
      errorContext: string
      routeSpecificErrors?: Record<number, string>
    }
  ): Promise<T | null> => {
    const { errorContext, routeSpecificErrors } = options

    isLoading.value = true
    error.value = null

    try {
      const response = await requestCall()
      return response.data
    } catch (err) {
      if (isAbortError(err)) {
        console.log(`${errorContext}: Request was aborted`)
        return null
      }

      const message = handleApiError(err, errorContext, routeSpecificErrors)
      error.value = message
      console.error(`${errorContext}: ${message}`, err)
      return null
    } finally {
      // 确保加载状态总是被重置
      isLoading.value = false
    }
  }

  const login = async (
    credentials: LoginRequest
  ): Promise<TokenResponse | null> => {
    return executeRequest<TokenResponse>(
      () =>
        fastapiClient.post(FASTAPI_CONFIG.ENDPOINTS.AUTH.LOGIN, credentials),
      {
        errorContext: 'User login',
        routeSpecificErrors: {
          401: '用户名或密码错误'
        }
      }
    )
  }

  const register = async (
    userData: RegisterRequest
  ): Promise<ApiResponse | null> => {
    return executeRequest<ApiResponse>(
      () =>
        fastapiClient.post(FASTAPI_CONFIG.ENDPOINTS.AUTH.REGISTER, userData),
      {
        errorContext: 'User registration',
        routeSpecificErrors: {
          400: 'Username or email already exists'
        }
      }
    )
  }

  const logout = async (): Promise<ApiResponse | null> => {
    return executeRequest<ApiResponse>(
      () => fastapiClient.post(FASTAPI_CONFIG.ENDPOINTS.AUTH.LOGOUT),
      {
        errorContext: 'User logout'
      }
    )
  }

  const getCurrentUser = async (): Promise<User | null> => {
    // For getCurrentUser, we want to handle 401 errors gracefully during auth initialization
    // We don't want to trigger the login dialog retry logic here
    try {
      const response = await fastapiClient.get(
        FASTAPI_CONFIG.ENDPOINTS.AUTH.ME,
        {
          timeout: 20000 // 增加超时时间到20秒，避免频繁超时
        }
      )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token is invalid or expired, return null to indicate user needs to login
        console.log(
          'getCurrentUser: Token verification failed (401), user needs to re-authenticate'
        )
        return null
      }
      // For other errors, still return null but log the error
      console.error('getCurrentUser: Unexpected error:', error)
      return null
    }
  }

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ): Promise<ApiResponse | null> => {
    return executeRequest<ApiResponse>(
      () =>
        fastapiClient.post(FASTAPI_CONFIG.ENDPOINTS.AUTH.CHANGE_PASSWORD, {
          current_password: currentPassword,
          new_password: newPassword
        }),
      {
        errorContext: 'Change password',
        routeSpecificErrors: {
          400: 'Current password is incorrect'
        }
      }
    )
  }

  const updateUserProfile = async (
    userData: UserUpdateRequest
  ): Promise<User | null> => {
    return executeRequest<User>(
      () => fastapiClient.put('/api/v1/users/me', userData),
      {
        errorContext: 'Update user profile',
        routeSpecificErrors: {
          400: 'Invalid user data or data already exists'
        }
      }
    )
  }

  // Token management utilities - now handled by authStore
  const setTokens = (_accessToken: string, _refreshToken: string) => {
    // This is now handled by authStore.setAuthData()
    console.log('setTokens called - should use authStore.setAuthData() instead')
  }

  const clearTokens = () => {
    // This is now handled by authStore.clearAuthData()
    console.log(
      'clearTokens called - should use authStore.clearAuthData() instead'
    )
  }

  const getAccessToken = (): string | null => {
    // This is now handled by authStore.getAccessToken()
    try {
      const authStore = useAuthStore()
      return authStore.getAccessToken()
    } catch (error) {
      console.warn('Failed to get access token from authStore:', error)
      return null
    }
  }

  const isAuthenticated = (): boolean => {
    try {
      const authStore = useAuthStore()
      return authStore.isAuthenticated
    } catch (error) {
      console.warn('Failed to check authentication status:', error)
      return false
    }
  }

  return {
    // State
    isLoading,
    error,

    // Methods
    login,
    register,
    logout,
    getCurrentUser,
    changePassword,
    updateUserProfile,
    resetLoadingState,

    // Token utilities
    setTokens,
    clearTokens,
    getAccessToken,
    isAuthenticated
  }
}
